import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  useEffect
} from 'react';
import type { PropsWithoutRef, RefObject, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  debounce,
  defaultThemeProp,
  Flex,
  getFocusables,
  focusableSelector,
  Icon,
  Progress,
  registerIcon,
  StyledButton,
  useAfterInitialEffect,
  useArrows,
  useI18n,
  useItemIntersection,
  useLiveLog,
  useOuterEvent
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';
import * as caretDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-down.icon';

import { isMessageListItem, isSystemMessageListItem, isTypeIndicatorListItem } from './Chat.types';
import type {
  ChatBodyHandleValue,
  ChatBodyListItemProps,
  ChatBodyProps,
  MessageProps
} from './Chat.types';
import Message from './Message';
import SystemMessage, { StyledSystemMessage } from './SystemMessage';
import TypeIndicator from './TypeIndicator';
import { StyledMessageContainer } from './Message.styles';

registerIcon(caretDownIcon);

export const NewMessageSeparatorId = 'new-message-separator';

const StyledMessageList = styled.div``;
const StyledSession = styled.ul``;

const StyledChatBody = styled.div(props => {
  const {
    theme: {
      base: {
        shadow: { high: shadowHigh, focus },
        colors: { white },
        spacing,
        palette
      }
    }
  } = props;
  return css`
    position: relative;
    overflow-y: hidden;

    > ${StyledButton} {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      box-shadow: ${shadowHigh};
      z-index: 1;
      background-color: ${white};
    }

    > ${StyledMessageList} {
      overflow-y: auto;
      list-style-type: none;
      height: 100%;

      > ${StyledSession} {
        padding-inline: 1rem;
        &:not(:last-child) {
          border-block-end: 0.0625rem solid ${palette['border-line']};
          margin-block-end: ${spacing};
          padding-block-end: ${spacing};
        }
      }
      ${StyledMessageContainer}, ${StyledSystemMessage} {
        &:focus-visible {
          box-shadow: ${focus};
          outline: none;
        }
        margin-block-end: ${spacing};
      }
    }
  `;
});

StyledChatBody.defaultProps = defaultThemeProp;

const ScrollToLatest = ({
  scrollContainerRef
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
}) => {
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight - scrollContainerRef.current.offsetHeight;
    }
  });
  return null;
};

interface ChatMessageProps extends Pick<ChatBodyProps, 'renderMarkdownContent'> {
  message: ChatBodyListItemProps;
  agentSerial: RefObject<{ [agentId: string]: number }>;
}

const ChatMessage = ({ agentSerial, message, renderMarkdownContent }: ChatMessageProps) => {
  const getAgentVariant = (
    senderId: MessageProps['senderId'],
    senderType: MessageProps['senderType'],
    direction: MessageProps['direction'] = 'in'
  ) => {
    if (agentSerial.current === null) return undefined;
    if (senderType === 'agent' && direction === 'in') {
      if (agentSerial.current[senderId] !== undefined) {
        return agentSerial.current[senderId];
      }

      const nextIndex = Object.entries(agentSerial.current).length;
      agentSerial.current[senderId] = nextIndex;
      return nextIndex;
    }
    return undefined;
  };

  if (isMessageListItem(message)) {
    const { id, senderType, direction, senderId } = message;
    const agentVariant = getAgentVariant(senderId, senderType, direction);
    return (
      <Message
        {...message}
        key={id}
        agentVariant={agentVariant}
        renderMarkdownContent={renderMarkdownContent}
      />
    );
  }

  if (isSystemMessageListItem(message)) {
    const extraProps: Record<string, string> = {};
    const { id } = message;
    if (id === NewMessageSeparatorId) {
      extraProps['data-new-message-separator'] = NewMessageSeparatorId;
    }

    return <SystemMessage {...message} key={id} {...extraProps} />;
  }

  if (isTypeIndicatorListItem(message)) {
    const { id, senderId, senderType } = message;
    const agentVariant = getAgentVariant(senderId, senderType);

    return <TypeIndicator {...message} key={id} agentVariant={agentVariant} />;
  }

  return null;
};

const ChatBody: ForwardRefForwardPropsComponent<ChatBodyProps> = forwardRef(function ChatBody(
  {
    transcripts,
    liveChat,
    unreadMessageCount = 0,
    onScrollToButtonClick,
    handle,
    loading,
    offset = -1,
    loadMore,
    renderMarkdownContent,
    ...restProps
  }: PropsWithoutRef<ChatBodyProps>,
  ref: ChatBodyProps['ref']
) {
  const t = useI18n();
  const conversationRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const focusInMessageList = useRef(false);
  const initialMessageListFocused = useRef(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const activeElementIndex = useRef(-1);
  const agentSerial = useRef<{ [agentId: string]: number }>({});
  const chatFocusableSelector = 'li[type="system"],li[type="message"],li[type="typing"]';

  const [messageList, setMessageList] = useState<(HTMLLIElement | null)[]>([]);
  const [verticalNav, setVerticalNav] = useState(true);

  const { announcePolite } = useLiveLog();

  const isScrolledToLatest: ChatBodyHandleValue['isScrolledToLatest'] = useCallback(() => {
    return conversationRef.current
      ? conversationRef.current.scrollHeight -
          conversationRef.current.scrollTop -
          conversationRef.current.offsetHeight <=
          30
      : true;
  }, [conversationRef.current]);

  const scrollToLatestMessage: ChatBodyHandleValue['scrollToLatestMessage'] = useCallback(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
      focusInMessageList.current = true;
      messageList[messageList.length - 1]?.focus();
      activeElementIndex.current = messageList.length - 1;
    }
  }, [conversationRef.current, messageList]);

  const [displayScrollLatest, setDisplayScrollToLatest] = useState(false);

  const onMessageListScroll = useCallback(() => {
    const isScrolled = isScrolledToLatest();
    if (scrollRef.current && isScrolled) {
      scrollRef.current = false;
      if (unreadMessageCount) {
        onScrollToButtonClick?.();
      }
    }

    if (!scrollRef.current && !isScrolled) {
      scrollRef.current = true;
    }

    setDisplayScrollToLatest(!isScrolled);
  }, [isScrolledToLatest(), unreadMessageCount]);

  const scrollToNewMessage: ChatBodyHandleValue['scrollToNewMessage'] = useCallback(() => {
    if (conversationRef.current) {
      setDisplayScrollToLatest(false);
      const newMessageIndicatorEle = conversationRef.current.querySelector<HTMLElement>(
        '[data-new-message-separator]'
      );
      if (
        newMessageIndicatorEle &&
        newMessageIndicatorEle.nextElementSibling instanceof HTMLElement
      ) {
        conversationRef.current.scrollTop = newMessageIndicatorEle.offsetTop;
        focusInMessageList.current = true;
        newMessageIndicatorEle.nextElementSibling.focus();
        activeElementIndex.current =
          messageList.findIndex(item => item?.hasAttribute('data-new-message-separator')) + 1;
      } else {
        scrollToLatestMessage();
      }
    }
  }, [conversationRef.current, messageList]);

  useImperativeHandle(
    handle,
    () => ({
      isScrolledToLatest,
      scrollToLatestMessage,
      scrollToNewMessage
    }),
    [isScrolledToLatest, scrollToLatestMessage, scrollToNewMessage]
  );

  useItemIntersection(
    conversationRef,
    offset,
    () => {
      loadMore?.();
    },
    ':scope > ul > li'
  );

  useEffect(() => {
    const focusableElements =
      conversationRef.current?.querySelectorAll<HTMLLIElement>(chatFocusableSelector);

    if (focusableElements) setMessageList(Array.from(focusableElements));

    if (focusableElements && initialMessageListFocused.current === false) {
      activeElementIndex.current = focusableElements.length - 1;
    }
  }, [transcripts.length, liveChat.length]);

  useEffect(() => {
    elementRef.current = messageList[activeElementIndex.current];
    if (verticalNav) {
      elementRef.current?.focus();
    } else {
      getFocusables(elementRef)[0]?.focus();
    }
  }, [verticalNav]);

  useArrows(
    conversationRef,
    {
      cycle: true,
      selector: ':scope > ul > li',
      dir: 'up-down',
      allowTabFocus: true
    },
    [transcripts, liveChat]
  );

  useArrows(
    elementRef,
    {
      cycle: true,
      selector: `li :is(${focusableSelector})`,
      dir: 'left-right',
      allowTabFocus: true
    },
    [transcripts, liveChat, verticalNav]
  );

  const focusSiblingElement = (currentElement: Element, isShiftKey = false) => {
    elementRef.current = currentElement as HTMLElement;
    const focusableElements = getFocusables(elementRef);
    if (focusableElements?.length > 0) {
      focusableElements?.[isShiftKey ? focusableElements.length - 1 : 0].focus();
    } else {
      const element = isShiftKey
        ? elementRef.current?.previousElementSibling
        : elementRef.current?.nextElementSibling;

      if (element) focusSiblingElement(element, isShiftKey);
    }
  };

  useOuterEvent('mousedown', [conversationRef], () => {
    focusInMessageList.current = false;
  });

  useAfterInitialEffect(() => {
    if (liveChat?.length > 0) {
      const unreadMessage = liveChat[liveChat.length - 1];
      if (
        isSystemMessageListItem(unreadMessage) ||
        (isMessageListItem(unreadMessage) && unreadMessage.direction === 'in')
      ) {
        announcePolite({
          message: t('new_message'),
          type: 'alert'
        });
      }
    }
  }, [liveChat]);

  return (
    <>
      <Flex
        {...restProps}
        as={StyledChatBody}
        item={{
          grow: 1
        }}
        container={{
          direction: 'column'
        }}
        ref={ref}
      >
        <StyledMessageList
          ref={conversationRef}
          onScroll={debounce(onMessageListScroll, 100)}
          onFocus={() => {
            if (!focusInMessageList.current) {
              initialMessageListFocused.current = true;
              elementRef.current = messageList[activeElementIndex.current];
              elementRef.current?.focus();
              focusInMessageList.current = true;
            }
          }}
          onMouseDown={(e: MouseEvent<HTMLDivElement>) => {
            focusInMessageList.current = true;
            const liElement = (e.target as HTMLElement).closest(chatFocusableSelector);
            if (liElement) {
              activeElementIndex.current = messageList.indexOf(liElement as HTMLLIElement);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              elementRef.current = messageList[activeElementIndex.current];
              if (getFocusables(elementRef).length > 0) {
                setVerticalNav(false);
              }
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              if (e.key === 'ArrowUp') {
                activeElementIndex.current =
                  activeElementIndex.current === 0
                    ? messageList.length - 1
                    : activeElementIndex.current - 1;
              } else {
                activeElementIndex.current =
                  activeElementIndex.current === messageList.length - 1
                    ? 0
                    : activeElementIndex.current + 1;
              }
              setVerticalNav(true);
            } else if (e.key === 'Tab') {
              e.preventDefault();
              focusInMessageList.current = false;
              if (e.shiftKey) {
                const prevElement = conversationRef?.current?.parentElement?.previousElementSibling;
                if (prevElement) focusSiblingElement(prevElement, true);
              } else if (unreadMessageCount > 0 || displayScrollLatest) {
                buttonRef?.current?.focus();
              } else {
                const nextElement = conversationRef.current?.parentElement?.nextElementSibling;
                if (nextElement) focusSiblingElement(nextElement);
              }
            }
          }}
        >
          {loading && <Progress as='li' placement='block' />}
          {transcripts.map(session => {
            return (
              <StyledSession key={session.id}>
                {session.messages.map(message => (
                  <ChatMessage
                    message={message}
                    agentSerial={agentSerial}
                    key={message.id}
                    renderMarkdownContent={renderMarkdownContent}
                  />
                ))}
              </StyledSession>
            );
          })}
          <StyledSession>
            {liveChat.map(message => (
              <ChatMessage
                message={message}
                agentSerial={agentSerial}
                key={message.id}
                renderMarkdownContent={renderMarkdownContent}
              />
            ))}
          </StyledSession>
        </StyledMessageList>
        {(unreadMessageCount > 0 || displayScrollLatest) && (
          <Button
            aria-label={
              unreadMessageCount > 0
                ? t('scroll_to_unread_messages')
                : t('scroll_to_latest_message')
            }
            onClick={() => {
              if (unreadMessageCount > 0) {
                scrollToNewMessage();
              } else {
                scrollToLatestMessage();
              }
              onScrollToButtonClick?.();
            }}
            icon={unreadMessageCount === 0}
            ref={buttonRef}
          >
            <Icon name='caret-down' />
            {unreadMessageCount > 0 && <>&nbsp; {t('new_messages')}</>}
          </Button>
        )}
      </Flex>
      {isScrolledToLatest() && <ScrollToLatest scrollContainerRef={conversationRef} />}
    </>
  );
});

export default ChatBody;
